/**
 * The header's server indicator: is the server reachable, and do this
 * device's changes reach it. Where the open map is stored (online or on this
 * device) is shown separately, next to the map's title (interfaces/classic/ui/map-storage.js).
 *
 * Pure, for tests: sync-orchestrator gathers the inputs and draws the result.
 *
 * Levels:
 *   ok       connected, changes saved on the server (green)
 *   busy     connecting or sending changes (neutral)
 *   problem  the server answers but changes don't get through (orange)
 *   offline  the server can't be reached (changes wait on this device)
 *   none     not signed in
 */
export const ServerLevel = Object.freeze({
  OK: 'ok',
  BUSY: 'busy',
  PROBLEM: 'problem',
  OFFLINE: 'offline',
  NONE: 'none',
});

/**
 * @param {object} input
 * @param {boolean} input.signedIn
 * @param {boolean} [input.quotaBlocked]  the open map or the account is over quota
 * @param {boolean} [input.syncPaused]    sync of the vault or of the open map is paused
 * @param {'online'|'local'|null} [input.mapStorage]  where the open map is stored
 * @param {string|null} [input.mapState]    open map's connection (ConnectionState)
 * @param {string|null} [input.vaultState]  vault connection (UserConnectionState)
 * @param {boolean} [input.sessionEnded]  the server ended this device's session
 *   (signed out from another device, password changed): sync stopped
 * @param {boolean} [input.serverBusy]  the server refused a save as busy; it is
 *   sent again shortly (changes kept on this device)
 * @param {boolean} [input.tooManyConnections]  the server refused a connection:
 *   this account has too many windows or devices connected
 * @param {'ok'|'degraded'|'unreachable'|null} [input.server]  last health probe; null: not probed
 * @param {number} [input.filesWaiting]  files attached offline, not sent yet
 * @param {boolean} [input.mayBeCleared]  the browser may delete what Orbital stores
 * @returns {{level: string, text: string, hint: string, params?: object, warning?: string}}
 *   text, hint and warning are i18n keys, `params` their placeholders;
 *   `warning` goes after the hint
 */
export function serverStatus(input) {
  const status = baseStatus(input);
  // Changes wait on this device (offline, not getting through, files to
  // send): say when the browser may delete them.
  const waiting = status.level === ServerLevel.OFFLINE || status.level === ServerLevel.PROBLEM
    || status.text === 'app.status.sendingFiles';
  if (input.mayBeCleared && input.signedIn && waiting) status.warning = 'vault.device.mayBeCleared';
  return status;
}

function baseStatus({
  signedIn,
  quotaBlocked = false,
  syncPaused = false,
  mapStorage = null,
  mapState = null,
  vaultState = null,
  server = null,
  filesWaiting = 0,
  tooManyConnections = false,
  serverBusy = false,
  sessionEnded = false,
}) {
  if (!signedIn) return { level: ServerLevel.NONE, text: 'app.status.notSignedIn', hint: 'app.status.hint.signedOut' };
  if (sessionEnded) return { level: ServerLevel.PROBLEM, text: 'app.status.sessionEnded', hint: 'app.status.hint.sessionEnded' };
  if (server === 'unreachable') return { level: ServerLevel.OFFLINE, text: 'html.header.offline', hint: 'app.status.hint.offline' };
  if (quotaBlocked) return { level: ServerLevel.PROBLEM, text: 'app.status.storageFull', hint: 'app.status.storageFullHint' };
  if (syncPaused) return { level: ServerLevel.PROBLEM, text: 'app.syncPaused.title', hint: 'app.status.syncPausedHint' };
  if (server === 'degraded') return { level: ServerLevel.PROBLEM, text: 'app.status.serverTrouble', hint: 'app.status.hint.serverTrouble' };
  // Refused, with the reason: the user can do something about it.
  if (tooManyConnections) return { level: ServerLevel.PROBLEM, text: 'app.status.tooManyConnections', hint: 'app.status.hint.tooManyConnections' };
  // Busy: the change is kept here and sent again shortly; nothing to do.
  if (serverBusy) return { level: ServerLevel.BUSY, text: 'app.status.serverBusy', hint: 'app.status.hint.serverBusy' };

  // An online map has its own connection; a map on this device only needs
  // the vault's (maps list, settings).
  const online = mapStorage !== 'local';
  const state = online ? mapState : vaultState;
  switch (state) {
    case 'synced':
    case 'connected':
      // Connected, with files attached offline still to send.
      if (filesWaiting > 0) {
        return { level: ServerLevel.BUSY, text: 'app.status.sendingFiles', hint: 'app.status.hint.filesWaiting', params: { count: filesWaiting } };
      }
      return {
        level: ServerLevel.OK,
        text: 'app.status.connected',
        hint: online ? 'app.status.hint.connectedOnline' : 'app.status.hint.connectedLocal',
      };
    case 'syncing':
      return { level: ServerLevel.BUSY, text: 'app.status.syncing', hint: 'app.status.hint.sending' };
    case 'connecting':
      return { level: ServerLevel.BUSY, text: 'app.status.connecting', hint: 'app.status.hint.connecting' };
    case 'reconnecting':
      return { level: ServerLevel.PROBLEM, text: 'app.status.reconnecting', hint: 'app.status.hint.reconnecting' };
    default:
      // Error or disconnected while the server may well be up: only a probe
      // tells "offline" from "not getting through".
      if (server === null) return { level: ServerLevel.BUSY, text: 'app.status.connecting', hint: 'app.status.hint.connecting' };
      return {
        level: ServerLevel.PROBLEM,
        text: state === 'error' ? 'app.status.syncFailed' : 'app.status.notSyncing',
        hint: 'app.status.hint.problem',
      };
  }
}

/** The tooltip: the hint, then the warning if any. `translate` is i18n's t. */
export function hintText(status, translate) {
  const hint = translate(status.hint, status.params);
  return status.warning ? `${hint} ${translate(status.warning)}` : hint;
}

/** Whether the status needs a server probe to be told apart (see serverStatus). */
export function needsProbe({ signedIn, mapStorage = null, mapState = null, vaultState = null }) {
  if (!signedIn) return false;
  const state = mapStorage !== 'local' ? mapState : vaultState;
  return !['synced', 'connected', 'syncing', 'connecting'].includes(state);
}
